import { useEffect, useRef, useState } from "react";
import { ArrowRight, Phone, Mail, CheckCircle2, ChevronLeft, ChevronRight, Quote, Loader2, Circle, Award, BadgeCheck, BarChart3, CalendarCheck, ClipboardCheck, Handshake, Infinity, Laptop, Layers, MonitorSmartphone, PenTool, Presentation, Route, Settings, TrendingUp, Users, Trophy, Sparkles, ShieldCheck, GraduationCap } from "lucide-react";

const ICONS_MAP = { Circle, Award, BadgeCheck, BarChart3, CalendarCheck, ClipboardCheck, Handshake, Infinity, Laptop, Layers, MonitorSmartphone, PenTool, Presentation, Route, Settings, TrendingUp, Users, Trophy, Sparkles, ShieldCheck, GraduationCap, CheckCircle2 };
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import { getAttribution, trackEvent } from "@/lib/analytics";
import { useLeadForm } from "@/lib/leadForms";
import ConsentCheckbox, { CONSENT_TEXT, validateConsent } from "@/components/ConsentCheckbox";
import LogoSlider from "@/components/LogoSlider";
import MediaImg from "@/components/MediaImg";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const NAVY = "#0B1526";
const YELLOW = "#FFC72C";

const scrollToForm = () => document.getElementById("corp-proposal-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
const scrollToAnchor = (link) => {
  if (!link) return;
  if (link.startsWith("#")) document.querySelector(link)?.scrollIntoView({ behavior: "smooth" });
  else window.location.href = link;
};

const LucideIcon = ({ name, className }) => {
  const I = ICONS_MAP[name] || Circle;
  return <I className={className} />;
};

const FIELD_PLACEHOLDERS = {
  email: "you@company.com", name: "John Doe", company: "Your Company",
  phone: "+91 98765 43210", requirement: "Tell us about your team's training needs…",
};

export function ProposalForm({ preview = false }) {
  const cardRef = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (preview) return;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [preview]);
  const cfg = useLeadForm("corporate_proposal", seen && !preview);
  const [form, setForm] = useState({ email: "", name: "", company: "", phone: "", requirement: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(true);

  const fields = (cfg.fields || []).filter((f) => f.visible !== false);

  const submit = async (e) => {
    e.preventDefault();
    if (preview) return;
    if (!validateConsent(consent)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", {
        name: form.name, email: form.email, phone: form.phone, company: form.company,
        message: form.requirement, source: "corporate_proposal",
        page: window.location.pathname, attribution: getAttribution(),
        consent_given: consent, consent_text: CONSENT_TEXT,
      });
      trackEvent("inquiry_submit", { form: "corporate_proposal" });
      setDone(true);
      toast.success(data.message || "Proposal requested");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={cardRef} id="corp-proposal-form" className="bg-white text-slate-900 rounded-2xl shadow-2xl p-6 sm:p-7 w-full max-w-md scroll-mt-24" data-testid="corp-proposal-form">
      {done ? (
        <div className="py-8 text-center" data-testid="corp-form-success">
          <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7" /></div>
          <p className="font-bold text-lg">{cfg.success_title || "Proposal requested"}</p>
          <p className="text-sm text-slate-500 mt-2">{cfg.success_message || "Our corporate training team will get back within 24 hours with a tailored plan."}</p>
        </div>
      ) : (
        <>
          <h3 className="text-xl font-extrabold tracking-tight" data-testid="corp-form-title">{cfg.title || "Get a Custom Training Proposal"}</h3>
          <p className="text-sm text-slate-500 mt-1.5" data-testid="corp-form-subtitle">{cfg.subtitle || "Share your requirements and our team will get back to you with a tailored plan."}</p>
          <form onSubmit={submit} className="flex flex-col gap-3.5 mt-5">
            {fields.map((f) => (
              <div key={f.key}>
                <label htmlFor={`corp-${f.key}`} className="text-xs font-bold text-slate-600">{f.label}{f.required ? " *" : ""}</label>
                {f.type === "textarea" ? (
                  <textarea id={`corp-${f.key}`} rows={3} required={!!f.required} placeholder={FIELD_PLACEHOLDERS[f.key] || ""}
                    value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC72C]"
                    data-testid={`corp-form-${f.key}`} />
                ) : (
                  <input id={`corp-${f.key}`} type={f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text"} required={!!f.required} placeholder={FIELD_PLACEHOLDERS[f.key] || ""}
                    value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC72C]"
                    data-testid={`corp-form-${f.key}`} />
                )}
              </div>
            ))}
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="corp-consent" />
            <button type="submit" disabled={loading || preview}
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFC72C] hover:bg-[#F5B800] disabled:opacity-50 text-[#17263B] font-extrabold text-sm px-5 py-3 transition-colors"
              data-testid="corp-form-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Sending…" : (cfg.button_label || "Request Proposal")}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
            <p className="text-[11px] text-slate-400 text-center">We respect your privacy. Your information is safe with us.</p>
          </form>
        </>
      )}
    </div>
  );
}

export function CorpHero({ section, interactive = true }) {
  if (!section) return null;
  const badges = section.badges || [];
  return (
    <section className="relative overflow-hidden text-white" style={{ backgroundColor: NAVY }} data-testid="corp-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="corp-hero-title">
            {section.title_black} <span style={{ color: YELLOW }}>{section.title_accent}</span>
          </h1>
          <p className="text-slate-300 mt-5 text-base sm:text-lg max-w-xl leading-relaxed" data-testid="corp-hero-subtitle">{section.subtitle}</p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <button onClick={interactive ? scrollToForm : undefined}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 font-extrabold text-sm text-[#17263B] transition-colors hover:bg-[#F5B800]" style={{ backgroundColor: YELLOW }}
              data-testid="corp-cta-primary">
              {section.cta_primary_label || "Discuss Training Needs"} <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={interactive ? () => scrollToAnchor(section.cta_secondary_link || "#solutions") : undefined}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 font-bold text-sm border border-white/40 text-white hover:bg-white/10 transition-colors"
              data-testid="corp-cta-secondary">
              {section.cta_secondary_label || "Explore Solutions"}
            </button>
          </div>
          {badges.length > 0 && (
            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-10" data-testid="corp-hero-badges">
              {badges.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-2 text-xs font-semibold text-slate-300" data-testid={`corp-hero-badge-${i}`}>
                  <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center" style={{ color: YELLOW }}>
                    <LucideIcon name={b.icon} className="w-4 h-4" />
                  </span>
                  {b.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="relative rounded-3xl overflow-hidden min-h-[420px] flex items-center justify-center lg:justify-end" data-testid="corp-hero-media">
          {section.image && <MediaImg src={section.image} alt="Corporate training session" className="absolute inset-0 w-full h-full object-cover" eager sizes="(max-width: 1024px) 100vw, 50vw" testid="corp-hero-image" />}
          <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${NAVY}E6 0%, ${NAVY}99 45%, ${NAVY}55 100%)` }} />
          {section.overlay_words && (
            <p className="absolute bottom-5 right-6 z-10 text-[11px] font-extrabold tracking-[0.3em] text-white/70" data-testid="corp-hero-overlay-words">{section.overlay_words}</p>
          )}
          <div className="relative z-10 p-5 sm:p-8 flex justify-end w-full"><ProposalForm preview={!interactive} /></div>
        </div>
      </div>
    </section>
  );
}

function SectionHead({ section, testid }) {
  return (
    <div className="text-center max-w-2xl mx-auto">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900" data-testid={`${testid}-title`}>{section.title}</h2>
      {section.subtitle && <p className="text-slate-500 mt-3 text-sm sm:text-base" data-testid={`${testid}-subtitle`}>{section.subtitle}</p>}
    </div>
  );
}

const IconChip = ({ name }) => (
  <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${YELLOW}33`, color: "#B8860B" }}>
    <LucideIcon name={name} className="w-6 h-6" />
  </span>
);

export function CorpMetrics({ section }) {
  if (!section) return null;
  const items = (section.items || []).filter((i) => i.visible !== false);
  if (!items.length) return null;
  return (
    <section className="bg-white border-b border-slate-100" data-testid="corp-metrics">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-4" data-testid={`corp-metric-${i}`}>
            <IconChip name={it.icon} />
            <div>
              <p className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{it.value}</p>
              <p className="text-xs sm:text-sm text-slate-500 font-semibold">{it.label}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function CorpSolutions({ section, interactive = true }) {
  if (!section) return null;
  const items = (section.items || []).filter((i) => i.visible !== false);
  if (!items.length) return null;
  return (
    <section id="solutions" className="bg-slate-50 scroll-mt-16" data-testid="corp-solutions">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <SectionHead section={section} testid="corp-solutions" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-7 hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`corp-solution-${i}`}>
              <IconChip name={it.icon} />
              <h3 className="text-lg font-extrabold text-slate-900 mt-5">{it.title}</h3>
              <p className="text-sm text-slate-500 mt-2.5 leading-relaxed">{it.text}</p>
              {it.cta_label && (
                <button onClick={interactive ? scrollToForm : undefined} className="mt-5 inline-flex items-center gap-1.5 text-sm font-extrabold text-[#17263B] hover:gap-2.5 transition-all" data-testid={`corp-solution-cta-${i}`}>
                  {it.cta_label} <ArrowRight className="w-4 h-4" style={{ color: "#E0A800" }} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CorpTech({ section }) {
  const [active, setActive] = useState(0);
  const items = (section?.items || []).filter((i) => i.visible !== false);
  if (!section || !items.length) return null;
  const cur = items[Math.min(active, items.length - 1)];
  const skills = (cur.skills || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const tools = (cur.tools || "").split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <section className="bg-white" data-testid="corp-tech">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <SectionHead section={section} testid="corp-tech" />
        <div className="flex gap-2.5 mt-10 overflow-x-auto pb-2 justify-start lg:justify-center" style={{ scrollbarWidth: "none" }} data-testid="corp-tech-tabs">
          {items.map((t, i) => (
            <button key={i} onClick={() => setActive(i)}
              className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${i === active ? "text-white" : "text-slate-600 border border-slate-200 hover:border-slate-400"}`}
              style={i === active ? { backgroundColor: NAVY } : {}}
              data-testid={`corp-tech-tab-${i}`}>{t.name}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mt-12" data-testid="corp-tech-panel">
          <div>
            <h3 className="text-2xl font-extrabold tracking-tight text-slate-900">{cur.heading}</h3>
            <p className="text-slate-500 mt-2">{cur.subtext}</p>
            {skills.length > 0 && (
              <>
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mt-7">Key Skills</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 mt-3">
                  {skills.map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid={`corp-tech-skill-${i}`}>
                      <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "#E0A800" }} /> {s}
                    </span>
                  ))}
                </div>
              </>
            )}
            {tools.length > 0 && (
              <>
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mt-7">Popular Tools</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {tools.map((t, i) => (
                    <span key={i} className="rounded-full bg-slate-100 px-3.5 py-1.5 text-xs font-bold text-slate-700" data-testid={`corp-tech-tool-${i}`}>{t}</span>
                  ))}
                </div>
              </>
            )}
          </div>
          {cur.image && <MediaImg src={cur.image} alt={cur.name} className="w-full h-72 sm:h-96 object-cover rounded-3xl" sizes="(max-width: 1024px) 100vw, 50vw" testid="corp-tech-image" />}
        </div>
      </div>
    </section>
  );
}

export function CorpWhy({ section }) {
  if (!section) return null;
  const items = (section.items || []).filter((i) => i.visible !== false);
  if (!items.length) return null;
  return (
    <section className="bg-slate-50" data-testid="corp-why">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <SectionHead section={section} testid="corp-why" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-shadow" data-testid={`corp-why-${i}`}>
              <IconChip name={it.icon} />
              <h3 className="font-extrabold text-slate-900 mt-4">{it.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{it.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CorpProcess({ section }) {
  if (!section) return null;
  const items = (section.items || []).filter((i) => i.visible !== false);
  if (!items.length) return null;
  return (
    <section className="bg-white" data-testid="corp-process">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <SectionHead section={section} testid="corp-process" />
        <div className="relative mt-14">
          <div className="hidden lg:block absolute top-8 left-[12%] right-[12%] border-t-2 border-dashed border-slate-200" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {items.map((it, i) => (
              <div key={i} className="relative text-center" data-testid={`corp-process-step-${i}`}>
                <span className="relative z-10 mx-auto w-16 h-16 rounded-full flex items-center justify-center font-extrabold text-lg text-[#17263B]" style={{ backgroundColor: YELLOW }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="mx-auto mt-4 w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600"><LucideIcon name={it.icon} className="w-5 h-5" /></span>
                <h3 className="font-extrabold text-slate-900 mt-3">{it.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5">{it.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function CorpDelivery({ section, interactive = true }) {
  if (!section) return null;
  const items = (section.items || []).filter((i) => i.visible !== false);
  if (!items.length) return null;
  return (
    <section className="bg-slate-50" data-testid="corp-delivery">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <SectionHead section={section} testid="corp-delivery" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-7 flex flex-col hover:shadow-lg transition-shadow" data-testid={`corp-delivery-${i}`}>
              <IconChip name={it.icon} />
              <h3 className="text-lg font-extrabold text-slate-900 mt-5">{it.title}</h3>
              <p className="text-sm text-slate-500 mt-2.5 leading-relaxed flex-1">{it.text}</p>
              <button onClick={interactive ? scrollToForm : undefined} className="self-start mt-5 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#E0A800] hover:text-[#E0A800] transition-colors" aria-label={`Enquire about ${it.title}`} data-testid={`corp-delivery-cta-${i}`}>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CorpTestimonials({ section }) {
  const [idx, setIdx] = useState(0);
  const items = (section?.items || []).filter((i) => i.visible !== false);
  if (!section || !items.length) return null;
  const cur = items[Math.min(idx, items.length - 1)];
  const go = (d) => setIdx((i) => (i + d + items.length) % items.length);
  return (
    <section className="bg-white" data-testid="corp-testimonials">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="corp-testimonials-title">{section.title}</h2>
          {section.subtitle && <p className="text-slate-500 mt-3 text-sm sm:text-base" data-testid="corp-testimonials-subtitle">{section.subtitle}</p>}
          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-7" data-testid="corp-testimonial-card">
            <Quote className="w-8 h-8" style={{ color: YELLOW }} />
            <p className="text-slate-700 leading-relaxed mt-4" data-testid="corp-testimonial-quote">{cur.quote}</p>
            <div className="flex items-center gap-3.5 mt-6">
              {cur.photo ? (
                <img src={cur.photo} alt={cur.name} className="w-12 h-12 rounded-full object-cover" loading="lazy" />
              ) : (
                <span className="w-12 h-12 rounded-full bg-[#17263B] text-white flex items-center justify-center font-extrabold text-sm">{(cur.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
              )}
              <div>
                <p className="font-extrabold text-slate-900 text-sm" data-testid="corp-testimonial-name">{cur.name}</p>
                <p className="text-xs text-slate-500" data-testid="corp-testimonial-role">{cur.role}</p>
              </div>
            </div>
          </div>
          {items.length > 1 && (
            <div className="flex items-center gap-3 mt-5">
              <button onClick={() => go(-1)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:border-[#E0A800] hover:text-[#E0A800] transition-colors" aria-label="Previous testimonial" data-testid="corp-testimonial-prev"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => go(1)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:border-[#E0A800] hover:text-[#E0A800] transition-colors" aria-label="Next testimonial" data-testid="corp-testimonial-next"><ChevronRight className="w-5 h-5" /></button>
              <div className="flex gap-1.5 ml-2">
                {items.map((_, i) => (
                  <span key={i} className={`w-2 h-2 rounded-full ${i === idx ? "bg-[#E0A800]" : "bg-slate-200"}`} data-testid={`corp-testimonial-dot-${i}`} />
                ))}
              </div>
            </div>
          )}
        </div>
        {section.image && <MediaImg src={section.image} alt="Client team" className="w-full h-72 sm:h-96 object-cover rounded-3xl" sizes="(max-width: 1024px) 100vw, 50vw" testid="corp-testimonial-image" />}
      </div>
    </section>
  );
}

export function CorpFaq({ section }) {
  if (!section) return null;
  const items = (section.items || []).filter((i) => i.visible !== false);
  if (!items.length) return null;
  return (
    <section className="bg-slate-50" data-testid="corp-faq">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <SectionHead section={section} testid="corp-faq" />
        <Accordion type="multiple" className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12 items-start">
          {items.map((it, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="rounded-2xl border border-slate-200 bg-white px-5" data-testid={`corp-faq-item-${i}`}>
              <AccordionTrigger className="text-left text-sm font-bold text-slate-900 hover:no-underline">{it.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-500 leading-relaxed">{it.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        {section.view_all_label && section.view_all_link && (
          <div className="text-right mt-6">
            <a href={section.view_all_link} className="inline-flex items-center gap-1.5 text-sm font-extrabold text-[#17263B] hover:gap-2.5 transition-all" data-testid="corp-faq-view-all">
              {section.view_all_label} <ArrowRight className="w-4 h-4" style={{ color: "#E0A800" }} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

export function CorpCta({ section, interactive = true }) {
  if (!section) return null;
  return (
    <section className="text-white" style={{ backgroundColor: NAVY }} data-testid="corp-final-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
        {section.eyebrow && <p className="text-xs font-extrabold tracking-[0.25em] text-slate-400" data-testid="corp-cta-eyebrow">{section.eyebrow}</p>}
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4" style={{ color: YELLOW }} data-testid="corp-cta-title">{section.title}</h2>
        {section.subtitle && <p className="text-slate-300 mt-4 text-base sm:text-lg" data-testid="corp-cta-subtitle">{section.subtitle}</p>}
        <button onClick={interactive ? scrollToForm : undefined}
          className="mt-8 inline-flex items-center gap-2 rounded-xl px-8 py-4 font-extrabold text-[#17263B] hover:bg-[#F5B800] transition-colors" style={{ backgroundColor: YELLOW }}
          data-testid="corp-cta-button">
          {section.cta_label || "Talk to Our Training Experts"} <ArrowRight className="w-4 h-4" />
        </button>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 mt-8 text-sm font-bold">
          {section.phone && (
            <a href={`tel:${section.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="corp-cta-phone">
              <Phone className="w-4 h-4" style={{ color: YELLOW }} /> {section.phone}
            </a>
          )}
          {section.email && (
            <a href={`mailto:${section.email}`} className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="corp-cta-email">
              <Mail className="w-4 h-4" style={{ color: YELLOW }} /> {section.email}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

export default function CorporateTraining() {
  const [sections, setSections] = useState(null);

  useSEO({
    title: "Corporate Training — Build a Future-Ready Workforce | Training Basket",
    description: "Tailored corporate training programs in AI, cloud, cybersecurity, data and software development. Customized team training, instructor-led workshops and certification programs for organizations.",
    canonical: `${window.location.origin}/corporate-training`,
  });

  useEffect(() => {
    trackEvent("page_view", { page: "corporate_training" });
    api.get("/website/sections").then((r) => {
      const m = {};
      (r.data.sections || []).forEach((s) => { m[s.key] = s; });
      setSections(m);
    }).catch(() => setSections({}));
  }, []);

  const s = (k) => sections?.[k];
  const ord = (k, d) => (typeof sections?.[k]?.order === "number" ? sections[k].order : d);

  if (!sections) return <div className="min-h-[70vh] bg-white" data-testid="corp-loading" />;

  return (
    <div className="flex flex-col bg-white" data-testid="corporate-training-page">
      <div style={{ order: ord("corporate_hero", 10) }}><CorpHero section={s("corporate_hero")} /></div>
      <div style={{ order: ord("corporate_trusted", 20) }}><LogoSlider sectionKey="corporate_trusted" /></div>
      <div style={{ order: ord("corporate_metrics", 30) }}><CorpMetrics section={s("corporate_metrics")} /></div>
      <div style={{ order: ord("corporate_solutions", 40) }}><CorpSolutions section={s("corporate_solutions")} /></div>
      <div style={{ order: ord("corporate_tech", 50) }}><CorpTech section={s("corporate_tech")} /></div>
      <div style={{ order: ord("corporate_why", 60) }}><CorpWhy section={s("corporate_why")} /></div>
      <div style={{ order: ord("corporate_process", 70) }}><CorpProcess section={s("corporate_process")} /></div>
      <div style={{ order: ord("corporate_delivery", 80) }}><CorpDelivery section={s("corporate_delivery")} /></div>
      <div style={{ order: ord("corporate_testimonials", 90) }}><CorpTestimonials section={s("corporate_testimonials")} /></div>
      <div style={{ order: ord("corporate_faq", 100) }}><CorpFaq section={s("corporate_faq")} /></div>
      <div style={{ order: ord("corporate_cta", 110) }}><CorpCta section={s("corporate_cta")} /></div>
    </div>
  );
}
